import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, RefreshCw, FileText } from 'lucide-react';
import { NavigationMenu } from '@/components/dashboard/navigation-menu';
import { useAuth } from '@/context/AuthContext';
import { API_BASE_URL, apiService, Session } from '@/lib/api';
import { ScrollingAnnouncementBar } from '@/components/dashboard/scrolling-announcement-bar';

interface BookingHistory {
  singles: SingleBooking[];
  rajshrees: any[];
  choices: any[];
}

interface SingleBooking {
  id: string;
  userId: string;
  number: number;
  qty: number;
  amount: number;
  cateogry: string;
  status: string;
  sessionId: string;
  createdAt: string;
  session: {
    name: string;
    date: string;
  };
}

export default function SingleList() {
  const [bookings, setBookings] = useState<SingleBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
        const { user, isAdmin } = useAuth()
      const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [balance, setBalance] = useState(0)
    const [sessions, setSessions] = useState<Session[]>([])
        const [activeSession, setActiveSession] = useState<Session | null>(null)
  const [selectedGameTime, setSelectedGameTime] = useState("Morning (13:00)")
  
  useEffect(() => {
    fetchInitialData();
  }, []);
  const fetchInitialData = async () => {
    try {
      setLoading(true)
      
      // Get active session
      const sessionResponse = await apiService.getActiveSession()
      if (sessionResponse.success && sessionResponse.data) {
        setActiveSession(sessionResponse.data)
        setSelectedGameTime(`${sessionResponse.data.name} (${sessionResponse.data.drawTime})`)
      }

      // Get user balance
      const balanceResponse = await apiService.getBalance()
      if (balanceResponse.success && balanceResponse.data) {
        setBalance(balanceResponse.data.balance)
      }

      // Get all sessions (for admin)
      if (isAdmin) {
        const sessionsResponse = await apiService.getAllSessions()
        if (sessionsResponse.success && sessionsResponse.data) {
          setSessions(sessionsResponse.data)
        }
      }

      // Initialize table based on selected game
    } catch (error) {
      console.error("Error fetching initial data:", error)
    } finally {
      setLoading(false)
    }
  }
  const fetchBookingHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/bookings/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch booking history');
      }

      const data: BookingHistory = await response.json();
      setBookings(data.singles || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load booking history');
      console.error('Error fetching booking history:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookingHistory();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      PENDING: { variant: 'secondary' as const, label: 'Pending' },
      WON: { variant: 'default' as const, label: 'Won' },
      LOST: { variant: 'destructive' as const, label: 'Lost' },
      CANCELLED: { variant: 'outline' as const, label: 'Cancelled' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { variant: 'secondary', label: status };
    
    return (
      <Badge variant={config.variant}>
        {config.label}
      </Badge>
    );
  };

  const calculateTotalAmount = () => {
    return bookings.reduce((total, booking) => total + booking.amount, 0);
  };

  const calculateTotalQuantity = () => {
    return bookings.reduce((total, booking) => total + booking.qty, 0);
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4 max-w-6xl">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              <span>Loading booking history...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-6xl">
            <header className="bg-gradient-to-r from-red-800 to-red-900 text-white py-3 px-4 sm:px-6 flex items-center justify-between shadow-md">
                              <div className="flex items-center gap-4">
                                 <NavigationMenu
                                          isMobileMenuOpen={isMobileMenuOpen} 
                                          setIsMobileMenuOpen={setIsMobileMenuOpen} 
                                          isAdmin={isAdmin}
                                        />
                                   </div>
                            </header>
                                              {/* Scrolling Announcement Bar */}
                                              <ScrollingAnnouncementBar 
                                                text={`🎯 Welcome to Three Circle 7 Lottery • Active Session: ${activeSession?.name || 'None'} • Balance: ₹${balance} • Play Responsibly • Good Luck! •`} 
                                              />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-2xl font-bold">Single Booking History</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Total {bookings.length} bookings • Total Amount: {formatCurrency(calculateTotalAmount())}
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={fetchBookingHistory} 
              variant="outline" 
              size="sm"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" size="sm">
              <FileText className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {bookings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No Single bookings found</p>
              <p className="text-sm">Your booking history will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Booking ID</TableHead>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Session</TableHead>
                    <TableHead>Number</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-center">Quantity</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell className="font-mono text-xs">
                        {booking.id.slice(-8)}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {formatDate(booking.createdAt)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{booking.session.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(booking.session.date).toLocaleDateString()}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground rounded-full font-bold">
                          {booking.number}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {booking.cateogry}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        {booking.qty}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(booking.amount)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(booking.status)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Summary */}
          {bookings.length > 0 && (
            <div className="mt-6 p-4 bg-muted/50 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{bookings.length}</div>
                  <div className="text-muted-foreground">Total Bookings</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(calculateTotalAmount())}
                  </div>
                  <div className="text-muted-foreground">Total Amount</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {calculateTotalQuantity()}
                  </div>
                  <div className="text-muted-foreground">Total Quantity</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {bookings.filter(b => b.status === 'PENDING').length}
                  </div>
                  <div className="text-muted-foreground">Pending</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {bookings.filter(b => b.status === 'WON').length}
                  </div>
                  <div className="text-muted-foreground">Won</div>
                </div>
              </div>

              {/* Category Breakdown */}
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-medium mb-3">Category Breakdown</h4>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
                  {['1st', '2nd', '3rd', '4th', '5th'].map((category) => {
                    const categoryBookings = bookings.filter(b => b.cateogry === category);
                    const categoryAmount = categoryBookings.reduce((sum, b) => sum + b.amount, 0);
                    return (
                      <div key={category} className="text-center p-2 bg-background rounded">
                        <div className="font-bold">{category}</div>
                        <div className="text-muted-foreground">
                          {categoryBookings.length} bets
                        </div>
                        <div className="font-medium text-green-600">
                          {formatCurrency(categoryAmount)}
                        </div>
                      </div>
                    );
                  })}
                  <div className="text-center p-2 bg-background rounded">
                    <div className="font-bold">Total</div>
                    <div className="text-muted-foreground">
                      {bookings.length} bets
                    </div>
                    <div className="font-medium text-green-600">
                      {formatCurrency(calculateTotalAmount())}
                    </div>
                  </div>
                </div>
              </div>

              {/* Number Distribution */}
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-medium mb-3">Number Distribution</h4>
                <div className="flex flex-wrap gap-1 justify-center">
                  {Array.from({ length: 10 }, (_, number) => {
                    const numberBookings = bookings.filter(b => b.number === number);
                    const numberAmount = numberBookings.reduce((sum, b) => sum + b.amount, 0);
                    if (numberBookings.length === 0) return null;
                    
                    return (
                      <div key={number} className="text-center p-2 bg-background rounded min-w-16">
                        <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full text-xs flex items-center justify-center mx-auto mb-1 font-bold">
                          {number}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {numberBookings.length}
                        </div>
                        <div className="text-xs font-medium text-green-600">
                          {formatCurrency(numberAmount)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}